import { useState, type ComponentProps, type FC, type MouseEvent } from 'react';

import { ChevronDownIcon } from 'lucide-react';

import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { TableCell } from '@/components/ui/table';

export const useCollapsibleState = <T extends string>(ids: T[] = []) => {
  const [expandedIds, setExpandedIds] = useState<Set<T>>(new Set());

  const isAllExpanded = ids.length > 0 && expandedIds.size === ids.length;
  const isNoneExpanded = expandedIds.size === 0;

  const expandAll = () => {
    setExpandedIds(new Set(ids));
  };
  const collapseAll = () => {
    setExpandedIds(new Set());
  };
  const toggleAll = () => {
    if (isAllExpanded) collapseAll();
    else expandAll();
  };

  const isExpanded = (id: T) => expandedIds.has(id);
  const toggle = (id: T) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getProps = (id: T) => ({
    open: expandedIds.has(id),
    onOpenChange: () => {
      toggle(id);
    },
  });

  return {
    expandedIds,
    isAllExpanded,
    isNoneExpanded,
    expandAll,
    collapseAll,
    toggleAll,
    isExpanded,
    toggle,
    getProps,
  };
};

interface CollapsibleCellProps extends ComponentProps<typeof TableCell> {
  triggerProps?: ComponentProps<typeof CollapsibleTrigger>;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export const CollapsibleCell: FC<CollapsibleCellProps> = ({
  triggerProps,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  className = '',
  children,
  ...cellProps
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledOpen ?? internalOpen;

  const handleToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onOpenChange) {
      //if  the parent is controlling the open state, we need to prevent the default behavior
      // CollapsibleTrigger will trigger twice and the parent won't register the change
      e.preventDefault();
    }
    triggerProps?.onClick?.(e);

    const newOpen = !isOpen;

    if (controlledOpen === undefined) setInternalOpen(newOpen);

    onOpenChange?.(newOpen);
  };

  return (
    <CollapsibleTrigger asChild {...triggerProps} onClick={handleToggle}>
      <TableCell {...cellProps} className="min-w-8 cursor-pointer group">
        <div className={className}>
          <ChevronDownIcon className="inline-block h-4 w-4 transition-transform duration-200 group-hover:scale-110 group-hover:-translate-y-0.5 group-data-[state=open]:rotate-180" />
          {children}
        </div>
      </TableCell>
    </CollapsibleTrigger>
  );
};
